import React from "react";
import Contact from "../contact";
import arrowIcon from "../../images/home/arrow-icon.svg";
import benifitIcon from "../../images/individual/benifitIcon.png";
import { FaVoteYea, FaCalendarAlt, FaExternalLinkAlt, FaArrowDown, FaArrowUp, FaUsers, FaMapMarkerAlt, FaChartLine, FaGlobe, FaCheckCircle, FaInfoCircle, FaExclamationTriangle, FaSuitcase, FaBalanceScale } from "react-icons/fa";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";

const CoalitionMigrationPolicyProposal = () => {
  return (
    <div>
      <Helmet>
        <title>Coalition's Migration Policy Proposal: 100,000 Reduction Plan - Immiget Global</title>
        <meta
          name="description"
          content="Explore the Coalition's proposal to reduce net overseas migration by 100,000, decreasing permanent migration from 185,000 to 140,000. Working holiday visas remain unaffected."
        />
        <meta 
          name="keywords" 
          content="coalition migration policy 2025, net overseas migration reduction, permanent migration cuts, working holiday visa protection, australian election policy" 
        />
      </Helmet>
      
      <div className="w-full h-90 font-Hellix bg-cover bg-center bg-image">
        <div className="max-w-[1440px] px-4 md:px-[75px] m-auto h-full">
          <div className="flex flex-col gap-2 md:gap-2 pt-10 md:pt-20 pb-[90px] h-full justify-center">
            <div className="text-white hellix-font text-center font-semibold text-[22px] md:text-[54px]">
              Coalition Migration Policy
            </div>
            <div className="text-white hellix-font text-center font-medium text-[16px] md:text-[24px] mb-4">
              Proposed 100,000 Migration Reduction
            </div>
            <div className="flex justify-center gap-2 md:gap-4">
              <div className="text-white hellix-font text-[10px] lg:text-sm flex items-center md:text-lg font-medium">
                <Link to="/" className="hover:underline">HOME</Link>
              </div>
              <div className="flex items-center">
                <img src={arrowIcon} className="w-[5.8px] md:w-auto" alt="" />
                <img src={arrowIcon} className="w-[5.8px] md:w-auto" alt="" />
              </div>
              <div className="text-white hellix-font text-[10px] lg:text-sm flex items-center md:text-lg font-medium">
                <Link to="/news" className="hover:underline">NEWS</Link>
              </div>
              <div className="flex items-center">
                <img src={arrowIcon} className="w-[5.8px] md:w-auto" alt="" />
                <img src={arrowIcon} className="w-[5.8px] md:w-auto" alt="" />
              </div>
              <div className="text-white hellix-font text-[10px] lg:text-sm flex items-center md:text-lg font-medium uppercase">
                Coalition Migration Policy
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Article Header */}
      <div className="container max-w-1440px mx-auto px-4 py-6 xl:py-10 lg:px-14">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-blue text-white p-3 rounded-full">
              <FaVoteYea className="text-xl" />
            </div>
            <div>
              <div className="flex items-center gap-2 text-blue text-sm hellix-font font-medium">
                <FaCalendarAlt className="text-xs" />
                <span>2025-26</span>
              </div>
              <div className="text-blue text-sm hellix-font">Policy Proposal</div>
            </div>
          </div>
          
          <h1 className="xl:text-[42px] text-[32px] text-blue font-bold hellix-font mb-6 leading-tight">
            Coalition's Migration Policy Proposal: 100,000 Reduction Plan
          </h1>
          
          <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-lg p-6 mb-8">
            <div className="flex items-start gap-4">
              <FaInfoCircle className="text-blue text-xl mt-1 flex-shrink-0" />
              <div>
                <h3 className="text-blue font-semibold hellix-font text-lg mb-2">Proposed Policy Change</h3>
                <p className="text-blue hellix-font text-lg leading-relaxed">
                  The Coalition has proposed reducing net overseas migration by <strong>100,000 if elected</strong>, aiming to decrease permanent migration from <strong>185,000 in 2024–25 to 140,000 in 2025–26</strong>. Working holiday visas will remain unaffected.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container max-w-1440px mx-auto px-4 lg:px-14">
        <div className="max-w-4xl mx-auto">
          
          {/* Overview Section */}
          <section className="mb-12">
            <div className="flex gap-2 mb-6">
              <div className="flex items-center">
                <img src={benifitIcon} alt="benefits" />
              </div>
              <h2 className="lg:text-[28px] text-2xl text-blue font-semibold hellix-font">
                Policy Overview
              </h2>
            </div>
            
            <div className="prose prose-lg max-w-none">
              <p className="text-blue hellix-font text-lg leading-relaxed mb-6">
                The Coalition's migration policy proposal represents a significant shift in Australia's approach to permanent migration. This policy aims to reduce pressure on housing and infrastructure while maintaining support for key sectors through selective visa categories.
              </p>
              
              <div className="grid md:grid-cols-3 gap-6 my-8">
                <div className="bg-white rounded-lg shadow-custom-white p-6 border-l-4 border-blue">
                  <div className="flex items-center gap-3 mb-4">
                    <FaArrowUp className="text-blue text-2xl" />
                    <h3 className="text-blue font-semibold hellix-font text-lg">Current Level</h3>
                  </div>
                  <p className="text-3xl font-bold text-blue hellix-font">185,000</p>
                  <p className="text-blue hellix-font text-sm">2024-25 permanent places</p>
                </div>
                
                <div className="bg-white rounded-lg shadow-custom-white p-6 border-l-4 border-red-500">
                  <div className="flex items-center gap-3 mb-4">
                    <FaArrowDown className="text-red-600 text-2xl" />
                    <h3 className="text-blue font-semibold hellix-font text-lg">Proposed Level</h3>
                  </div>
                  <p className="text-3xl font-bold text-red-600 hellix-font">140,000</p>
                  <p className="text-blue hellix-font text-sm">2025-26 proposed places</p>
                </div>
                
                <div className="bg-white rounded-lg shadow-custom-white p-6 border-l-4 border-orange-500">
                  <div className="flex items-center gap-3 mb-4">
                    <FaChartLine className="text-orange-600 text-2xl" />
                    <h3 className="text-blue font-semibold hellix-font text-lg">Reduction</h3>
                  </div>
                  <p className="text-3xl font-bold text-orange-600 hellix-font">-45,000</p>
                  <p className="text-blue hellix-font text-sm">Decrease in places</p>
                </div>
              </div>
            </div>
          </section>

          {/* Key Policy Points */}
          <section className="mb-12">
            <h2 className="lg:text-[28px] text-2xl text-blue font-semibold hellix-font mb-6 flex items-center gap-2">
              <FaBalanceScale className="text-blue" />
              Key Policy Points
            </h2>
            
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-red-50 to-white rounded-lg p-6">
                <div className="flex items-start gap-4">
                  <FaArrowDown className="text-red-500 text-xl mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-blue font-semibold hellix-font text-lg mb-2">Permanent Migration Reduction</h3>
                    <p className="text-blue hellix-font leading-relaxed">
                      The proposal targets a significant reduction in permanent migration numbers, from 185,000 places in 2024-25 to 140,000 in 2025-26, representing a 24% decrease in the Migration Program.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-gradient-to-r from-green-50 to-white rounded-lg p-6">
                <div className="flex items-start gap-4">
                  <FaCheckCircle className="text-green-500 text-xl mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-blue font-semibold hellix-font text-lg mb-2">Working Holiday Visas Protected</h3>
                    <p className="text-blue hellix-font leading-relaxed">
                      The Coalition has clarified that working holiday visas will remain unaffected, acknowledging the essential role of backpackers in supporting regional industries like tourism, hospitality, and agriculture.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-gradient-to-r from-blue-50 to-white rounded-lg p-6">
                <div className="flex items-start gap-4">
                  <FaMapMarkerAlt className="text-blue text-xl mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-blue font-semibold hellix-font text-lg mb-2">Regional Industry Support</h3>
                    <p className="text-blue hellix-font leading-relaxed">
                      Recognition of the vital contribution that temporary workers make to regional economies, particularly in sectors that rely heavily on seasonal and temporary labor.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Impact Analysis */}
          <section className="mb-12">
            <h2 className="lg:text-[28px] text-2xl text-blue font-semibold hellix-font mb-6 flex items-center gap-2">
              <FaChartLine className="text-blue" />
              Potential Impact Analysis
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-6">
                <div className="bg-white rounded-lg shadow-custom-white p-6">
                  <h3 className="text-blue font-semibold hellix-font text-lg mb-4 flex items-center gap-2">
                    <FaCheckCircle className="text-green-500" />
                    Potential Benefits
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <FaCheckCircle className="text-green-500 text-sm mt-1 flex-shrink-0" />
                      <span className="text-blue hellix-font text-sm">Reduced pressure on housing markets</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <FaCheckCircle className="text-green-500 text-sm mt-1 flex-shrink-0" />
                      <span className="text-blue hellix-font text-sm">Less strain on infrastructure</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <FaCheckCircle className="text-green-500 text-sm mt-1 flex-shrink-0" />
                      <span className="text-blue hellix-font text-sm">Potential wage growth in some sectors</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <FaCheckCircle className="text-green-500 text-sm mt-1 flex-shrink-0" />
                      <span className="text-blue hellix-font text-sm">More focus on local workforce development</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="space-y-6">
                <div className="bg-white rounded-lg shadow-custom-white p-6">
                  <h3 className="text-blue font-semibold hellix-font text-lg mb-4 flex items-center gap-2">
                    <FaExclamationTriangle className="text-orange-500" />
                    Potential Challenges
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <FaExclamationTriangle className="text-orange-500 text-sm mt-1 flex-shrink-0" />
                      <span className="text-blue hellix-font text-sm">Skills shortages in critical sectors</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <FaExclamationTriangle className="text-orange-500 text-sm mt-1 flex-shrink-0" />
                      <span className="text-blue hellix-font text-sm">Reduced economic growth potential</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <FaExclamationTriangle className="text-orange-500 text-sm mt-1 flex-shrink-0" />
                      <span className="text-blue hellix-font text-sm">Impact on aging population dynamics</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <FaExclamationTriangle className="text-orange-500 text-sm mt-1 flex-shrink-0" />
                      <span className="text-blue hellix-font text-sm">Competitive disadvantage for talent attraction</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Sector-Specific Impact */}
          <section className="mb-12">
            <h2 className="lg:text-[28px] text-2xl text-blue font-semibold hellix-font mb-6">
              Sector-Specific Implications
            </h2>
            
            <div className="grid gap-6">
              <div className="bg-green-50 rounded-lg p-6">
                <div className="flex items-start gap-4">
                  <FaSuitcase className="text-green-600 text-xl mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-blue font-semibold hellix-font text-lg mb-3">Protected Sectors</h3>
                    <p className="text-blue hellix-font mb-4">
                      The Coalition has specifically protected certain visa categories, recognizing their economic importance:
                    </p>
                    <div className="grid md:grid-cols-2 gap-4">
                      <ul className="space-y-2">
                        <li className="flex items-center gap-2">
                          <FaCheckCircle className="text-green-500 text-sm flex-shrink-0" />
                          <span className="text-blue hellix-font">Working Holiday visas (417/462)</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <FaCheckCircle className="text-green-500 text-sm flex-shrink-0" />
                          <span className="text-blue hellix-font">Regional tourism industry</span>
                        </li>
                      </ul>
                      <ul className="space-y-2">
                        <li className="flex items-center gap-2">
                          <FaCheckCircle className="text-green-500 text-sm flex-shrink-0" />
                          <span className="text-blue hellix-font">Hospitality sector support</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <FaCheckCircle className="text-green-500 text-sm flex-shrink-0" />
                          <span className="text-blue hellix-font">Agricultural workforce</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-orange-50 rounded-lg p-6">
                <div className="flex items-start gap-4">
                  <FaExclamationTriangle className="text-orange-600 text-xl mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-blue font-semibold hellix-font text-lg mb-3">Potentially Affected Areas</h3>
                    <p className="text-blue hellix-font mb-4">
                      Other visa categories and sectors may face increased competition or reduced allocations:
                    </p>
                    <div className="grid md:grid-cols-2 gap-4">
                      <ul className="space-y-2">
                        <li className="flex items-center gap-2">
                          <FaArrowDown className="text-orange-500 text-sm flex-shrink-0" />
                          <span className="text-blue hellix-font">Skilled independent migration</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <FaArrowDown className="text-orange-500 text-sm flex-shrink-0" />
                          <span className="text-blue hellix-font">Family stream visas</span>
                        </li>
                      </ul>
                      <ul className="space-y-2">
                        <li className="flex items-center gap-2">
                          <FaArrowDown className="text-orange-500 text-sm flex-shrink-0" />
                          <span className="text-blue hellix-font">Business and investment visas</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <FaArrowDown className="text-orange-500 text-sm flex-shrink-0" />
                          <span className="text-blue hellix-font">Humanitarian program</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Timeline and Implementation */}
          <section className="mb-12">
            <h2 className="lg:text-[28px] text-2xl text-blue font-semibold hellix-font mb-6">
              Proposed Timeline
            </h2>
            
            <div className="bg-white rounded-lg shadow-custom-white p-6">
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="bg-blue text-white w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm">1</div>
                  <div>
                    <h3 className="text-blue font-semibold hellix-font text-lg mb-2">Election Commitment</h3>
                    <p className="text-blue hellix-font">Coalition makes migration reduction a key election policy platform</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="bg-blue text-white w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm">2</div>
                  <div>
                    <h3 className="text-blue font-semibold hellix-font text-lg mb-2">2025-26 Implementation</h3>
                    <p className="text-blue hellix-font">If elected, reduction to 140,000 permanent places would take effect</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="bg-orange-500 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm">3</div>
                  <div>
                    <h3 className="text-blue font-semibold hellix-font text-lg mb-2">Policy Review</h3>
                    <p className="text-blue hellix-font">Ongoing assessment of impact and potential adjustments</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Comparison with Current Policy */}
          <section className="mb-12">
            <h2 className="lg:text-[28px] text-2xl text-blue font-semibold hellix-font mb-6">
              Policy Comparison
            </h2>
            
            <div className="bg-white rounded-lg shadow-custom-white p-6">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b-2 border-blue">
                      <th className="text-left py-4 px-2 text-blue hellix-font font-semibold">Aspect</th>
                      <th className="text-center py-4 px-2 text-blue hellix-font font-semibold">Current Policy</th>
                      <th className="text-center py-4 px-2 text-blue hellix-font font-semibold">Coalition Proposal</th>
                      <th className="text-center py-4 px-2 text-blue hellix-font font-semibold">Change</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-gray-200">
                      <td className="py-4 px-2 text-blue hellix-font">Permanent Migration</td>
                      <td className="text-center py-4 px-2 text-blue hellix-font">185,000</td>
                      <td className="text-center py-4 px-2 text-red-600 hellix-font font-semibold">140,000</td>
                      <td className="text-center py-4 px-2 text-red-600 hellix-font">-45,000</td>
                    </tr>
                    <tr className="border-b border-gray-200">
                      <td className="py-4 px-2 text-blue hellix-font">Working Holiday Visas</td>
                      <td className="text-center py-4 px-2 text-blue hellix-font">Current levels</td>
                      <td className="text-center py-4 px-2 text-green-600 hellix-font font-semibold">Protected</td>
                      <td className="text-center py-4 px-2 text-green-600 hellix-font">No change</td>
                    </tr>
                    <tr className="border-b border-gray-200">
                      <td className="py-4 px-2 text-blue hellix-font">Implementation</td>
                      <td className="text-center py-4 px-2 text-blue hellix-font">2024-25</td>
                      <td className="text-center py-4 px-2 text-blue hellix-font">2025-26</td>
                      <td className="text-center py-4 px-2 text-blue hellix-font">If elected</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </section>


          {/* Official Resources */}
          <section className="mb-12">
            <h2 className="lg:text-[28px] text-2xl text-blue font-semibold hellix-font mb-6">
              Official Resources & Further Reading
            </h2>
            
            <div className="bg-blue-50 rounded-lg p-6">
              <p className="text-blue hellix-font mb-4">
                For more information about the Coalition's migration policy proposal:
              </p>
              <a
                href="https://www.theguardian.com/australia-news/2025/apr/27/nationals-senator-bridget-mckenize-insists-coalition-wont-cut-working-holiday-visas"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-blue font-medium hellix-font hover:text-blue-700 transition-colors"
              >
                <FaExternalLinkAlt className="text-sm" />
                The Guardian - Coalition Migration Policy Details
              </a>
            </div>
          </section>

          {/* Related Articles */}
          <section className="mb-12">
            <h2 className="lg:text-[28px] text-2xl text-blue font-semibold hellix-font mb-6">
              Related Immigration News
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <Link to="/news/migration-program-2024-25" className="bg-white rounded-lg shadow-custom-white p-6 hover:shadow-lg transition-all duration-300">
                <div className="flex items-center gap-3 mb-3">
                  <FaArrowUp className="text-blue text-lg" />
                  <span className="text-blue text-sm hellix-font font-medium">Current Program</span>
                </div>
                <h3 className="text-blue font-semibold hellix-font text-lg mb-2">Migration Program 2024–25: Focus on Skills and Regional Australia</h3>
                <p className="text-blue hellix-font text-sm">Current government policy with 185,000 places allocated for permanent migration...</p>
              </Link>
              
              <Link to="/news" className="bg-white rounded-lg shadow-custom-white p-6 hover:shadow-lg transition-all duration-300">
                <div className="flex items-center gap-3 mb-3">
                  <FaGlobe className="text-blue text-lg" />
                  <span className="text-blue text-sm hellix-font font-medium">All News</span>
                </div>
                <h3 className="text-blue font-semibold hellix-font text-lg mb-2">View All Immigration News</h3>
                <p className="text-blue hellix-font text-sm">Stay updated with the latest immigration policies and political developments...</p>
              </Link>
            </div>
          </section>
        </div>
      </div>

      {/* Contact Section */}
      <Contact source="Coalition Migration Policy Proposal Blog" />
    </div>
  );
};

export default CoalitionMigrationPolicyProposal;